// /components/EMenuManager.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Trash2,
  Edit,
  Save,
  UploadCloud, // Changed icon for file upload
  Loader2,
  AlertTriangle,
  CheckCircle,
  FileText, // Icon for PDF/Document
  Printer, // New icon for print button
} from "lucide-react";
// Assuming these helper libraries exist in your project:
import { getApiClient, IS_DEV } from "../../libs/axiosInstance";
import { loadAuth } from "../../libs/authMgmt";
import { getSubdomain } from "../../libs/helper";
import { QRCodeSVG } from 'qrcode.react'; // ✅ CORRECT IMPORT
// /components/EMenuManager.js (Near the top imports)
import { useReactToPrint } from 'react-to-print';


// --- Configuration ---
const companyDetails = loadAuth("company");
const subdomain = IS_DEV ? companyDetails?.subdomain : getSubdomain();
const BASE_API_URL = "/api/companies/" + subdomain + "/e-menus"; 
const COMPANY_INFO = {
    name: companyDetails?.name || "Your Company Name",
    contactNumber: companyDetails?.contact_number, 
    address: companyDetails?.address,           
    logoUrl: companyDetails?.companyLogo,      
    subdomain: subdomain,
    phone: companyDetails?.phone
};

// --- Sub-Component: Add/Edit Modal (same as before) ---
// ... (AddEditModal content remains the same as in the previous response's EMenuManager.js)
const AddEditModal = ({
    isOpen,
    onClose,
    onSubmit,
    menuToEdit,
    isLoading,
  }) => {
    // name maps to EMenu.name
    // docFile maps to the new PDF file upload
    // currentDocUrl maps to EMenu.docUrl
    const [name, setName] = useState("");
    const [docFile, setDocFile] = useState(null);
    const [currentDocUrl, setCurrentDocUrl] = useState("");
    const [error, setError] = useState("");
  
    const isEditMode = !!menuToEdit;
  
    useEffect(() => {
      if (menuToEdit) {
        setName(menuToEdit.name || "");
        setCurrentDocUrl(menuToEdit.docUrl || "");
        setDocFile(null); // Reset file input when opening for edit
      } else {
        setName("");
        setCurrentDocUrl("");
        setDocFile(null);
      }
      setError("");
    }, [menuToEdit, isOpen]);
  
    const handleSubmit = (e) => {
      e.preventDefault();
      setError("");
  
      if (!name.trim()) {
        setError("E-Menu name is required.");
        return;
      }
  
      if (!isEditMode && !docFile) {
        setError("A PDF document file is required for a new e-menu.");
        return;
      }
      
      if (isEditMode && !docFile && name.trim() === menuToEdit.name) {
          setError("Please change the name or upload a new PDF document.");
          return;
      }
  
  
      const formData = new FormData();
      formData.append("name", name.trim());
      
      if (docFile) {
          // Use 'document' as the key for the file in the backend
          formData.append("document", docFile);
      }
      
      if (isEditMode) {
        formData.append("id", menuToEdit._id);
      }
  
      onSubmit(formData, isEditMode);
    };
  
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Check for file type (MIME type for PDF)
        if (file.type !== "application/pdf") {
          setError("Only Iamge or PDF files are allowed for e-menus.");
          setDocFile(null);
          e.target.value = null;
          return;
        }
        
        if (file.size > 10 * 1024 * 1024) { // Increased limit for a menu PDF (10MB)
          setError("PDF file size must not exceed 10MB.");
          setDocFile(null);
          e.target.value = null;
          return;
        }
        setDocFile(file);
        setError("");
      }
    };
  
    if (!isOpen) return null;
  
    return (
      // Mimic Bootstrap Modal: fixed backdrop and modal container
      <div
        className="modal d-block fade show"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3">
            {/* Modal Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditMode ? "Edit E-Menu" : "Upload New E-Menu"}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
  
            {/* Modal Body */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div
                    className="alert alert-danger d-flex align-items-center"
                    role="alert"
                  >
                    <AlertTriangle size={20} className="me-2" />
                    <div>{error}</div>
                  </div>
                )}
  
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Menu Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    disabled={isLoading}
                    required
                  />
                </div>
  
                <div className="mb-3">
                  <label htmlFor="document" className="form-label">
                    PDF E-Menu File ({isEditMode ? "Optional Update" : "Required"})
                  </label>
                  <input
                    type="file"
                    id="document"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="form-control"
                    disabled={isLoading}
                  />
  
                  {(currentDocUrl || docFile) && (
                    <div className="mt-3 p-3 bg-light border rounded d-flex align-items-center">
                      <FileText size={32} className="text-danger me-3 flex-shrink-0" />
                      <div className="flex-grow-1">
                        <span className="fw-medium d-block">
                          {docFile ? docFile.name : `Current Document`}
                        </span>
                        <small className="text-muted text-truncate">
                          {docFile ? `New File Ready to Upload` : `Path: ${currentDocUrl.length > 50 ? currentDocUrl.substring(0,50) + "..." : currentDocUrl}`}
                        </small>
                      </div>
                    </div>
                  )}
                  {!isEditMode && (
                    <small className="form-text text-muted">
                      Maximum size 10MB. Image or PDF files are accepted.
                    </small>
                  )}
                </div>
              </div>
  
              {/* Modal Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2
                        size={18}
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="me-2" />{" "}
                      {isEditMode ? "Save Changes" : "Upload Menu"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };
// --- END Add/Edit Modal ---

const QRDisplayModal = ({ isOpen, onClose, menu, company }) => {
    // 1. Create a ref for the printable content
    const componentRef = useRef(null); 

    // Safely access menuLink
    const menuLink = menu?.docUrl || ""; 
    
    // 2. DEFINE THE HOOK FIRST (unconditionally)
    const handlePrint = useReactToPrint({
        // CRITICAL: Point the content property to the ref's current value
        contentRef: componentRef, 
        documentTitle: `QR_Code_${menu?.name.replace(/\s/g, '_') || 'Menu'}`,
        
        // Use simpler pageStyle that relies on the package's DOM isolation.
        // The package handles hiding the non-printed elements for us.
        pageStyle: `
            @page { 
                size: auto; 
                margin: 0; 
            }
            /* Isolate the printed content */
            .qr-code-container, .qr-code-container > svg{
                max-width: 100%;
            }
            h4{
                font-size: 1.2em;

            }
            .qr-page-content {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 100%;
                padding: 40px; /* Add some margin from the paper edge */
            }
        `,
    });

    // 3. Conditional Return
    if (!isOpen || !menu) return null; 

    // --- REMOVED: const handlePrintCSS = () => { window.print(); };

    return (
        // Modal Backdrop
        <div
            className="modal d-block fade show"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }} 
        >
            <div className="modal-dialog modal-md modal-dialog-centered">
                {/* Note: Removed 'no-print' from modal-content to allow printing its children */}
                <div className="modal-content border-0 rounded-3"> 
                    
                    {/* Modal Header (Non-Printable) */}
                    {/* CRITICAL: We need the 'no-print' class on the things we DON'T want to print */}
                    <div className="modal-header no-print">
                        <h5 className="modal-title">Printable QR Code for: {menu.name}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>

                    {/* Modal Body - PRINT AREA */}
                    {/* ATTACH THE REF HERE. This is the only part that will be printed. */}
                    <div className="modal-body p-4 qr-page-content" ref={componentRef}> 
                        
                        {/* --- REMOVED: All the internal <style> blocks --- */}
                        
                        <div className="text-center qr-detail" style={{ width: '100%', maxWidth: '350px', margin: 'auto' }}>
                            
                            {/* Company Logo and Title */}
                            <div className="mb-4">
                                {company.logoUrl && (
                                    <img
                                        src={company.logoUrl}
                                        alt={`${company.name} Logo`}
                                        className="mb-2"
                                        style={{ maxWidth: '120px', objectFit: 'contain' }}
                                    />
                                )}
                                <h3 className="mb-0" style={{ fontWeight: 'bold' }}>
                                    {company.name}
                                </h3>
                                <p className="text-muted small">Scan for our Digital Menu</p>
                            </div>

                            {/* QR Code */}
                            <div 
                                className="qr-code-container"
                                style={{ 
                                    border: '3px solid #333', 
                                    display: 'inline-block', 
                                    padding: '15px', 
                                    borderRadius: '8px' 
                                }}
                            >
                                <QRCodeSVG 
                                    value={menuLink}
                                    size={250} 
                                    level="H" 
                                    bgColor="#FFFFFF"
                                    fgColor="#000000"
                                />
                            </div>

                            <h4 className="mt-4">{menu.name}</h4>
                            
                            {/* Contact Details */}
                            <div className="mt-4 pt-3 border-top text-start small">
                                <p className="mb-1">
                                    <span className="fw-bold">Address:</span> {company.address || 'Not provided'}
                                </p>
                                <p className="mb-1">
                                    <span className="fw-bold">Contact:</span> {company.phone || 'Not provided'}
                                </p>
                                <p className="text-muted mt-2 d-none">
                                    Link: <span className="text-break">{menuLink}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Modal Footer (Non-Printable) */}
                    {/* CRITICAL: Needs the 'no-print' class */}
                    <div className="modal-footer justify-content-between no-print">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        {/* Use the correct handlePrint hook from the package! */}
                        <button type="button" className="btn btn-primary d-flex align-items-center" onClick={handlePrint}>
                            <Printer size={18} className="me-2" /> Print QR Page
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// --- Main Component: EMenuManager ---
export default function EMenuManager({ apiUrl = BASE_API_URL }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [globalSuccess, setGlobalSuccess] = useState("");

  // Modals/Dialogs State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuToEdit, setMenuToEdit] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false); // New state for QR modal
  const [menuToShowQR, setMenuToShowQR] = useState(null);    // New state for QR data

  // --- Data Fetching ---
  // ... (fetchMenus remains the same)

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setGlobalError("");
    try {
      const client = await getApiClient();
      // NOTE: Using response.data.data because the API response now includes { success, data: menus }
      const response = await client.get(apiUrl); 
      setMenus(response.data.data || []);
    } catch (err) {
      setGlobalError("Failed to load e-menus. Please try refreshing.");
      console.error("Fetch error:", err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);


  // --- New QR Handlers ---
  const handleShowQr = (menu) => {
    setMenuToShowQR(menu);
    setIsQRModalOpen(true);
  };

  const handleCloseQRModal = () => {
    setIsQRModalOpen(false);
    setMenuToShowQR(null);
  };
  // -----------------------
  
// --- Modal Handlers ---
const handleOpenModal = (menu = null) => {
    setMenuToEdit(menu); // Sets the item for edit mode
    setIsModalOpen(true);
    setGlobalError("");
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setMenuToEdit(null);
  };
  
  // --- Delete Handlers ---
  const handleConfirmDelete = (menu) => {
    setItemToDelete(menu); // Sets the item for deletion
    setShowConfirm(true);
  };
  
  const handleCancelDelete = () => {
    setShowConfirm(false);
    setItemToDelete(null);
  };
  const handleModalSubmit = async (formData, isEditMode) => {
    setLoading(true);
    setGlobalError("");
    setGlobalSuccess("");

    try {
      const client = await getApiClient();
      let response;

      const headers = { "Content-Type": "multipart/form-data" };

      if (isEditMode) {
        const id = formData.get("id");
        // PUT request for update
        response = await client.put(`${apiUrl}?id=${id}`, formData, { headers });

        setMenus((prev) =>
          prev.map((menu) =>
            menu._id === id ? { ...menu, ...response.data.menu, _id: id } : menu
          ).sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)) // Sort by latest upload
        );
      } else {
        // POST request for create
        response = await client.post(apiUrl, formData, { headers });
        setMenus((prev) => [response.data.menu, ...prev]); // Add new menu to the top
      }

      setGlobalSuccess(response.data.message || "Operation successful!");
      handleCloseModal();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "A network error occurred during saving.";
      setGlobalError(message);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setGlobalSuccess("");
        setGlobalError("");
      }, 5000);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setLoading(true);
    setShowConfirm(false);
    setGlobalError("");
    setGlobalSuccess("");

    try {
      const client = await getApiClient();
      const response = await client.delete(`${apiUrl}?id=${itemToDelete._id}`);

      setMenus((prev) => prev.filter((menu) => menu._id !== itemToDelete._id));

      setGlobalSuccess(response.data.message || "E-Menu deleted successfully.");
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to delete e-menu.";
      setGlobalError(message);
    } finally {
      setLoading(false);
      setItemToDelete(null);
      setTimeout(() => {
        setGlobalSuccess("");
        setGlobalError("");
      }, 5000);
    }
  };

  // --- Render Functions (renderMenuCard update) ---

  const renderStatusMessage = (message, type) => (
    <div
      className={`alert ${
        type === "error" ? "alert-danger" : "alert-success"
      } d-flex align-items-center my-3`}
      role="alert"
    >
      {type === "error" ? (
        <AlertTriangle size={20} className="me-2" />
      ) : (
        <CheckCircle size={20} className="me-2" />
      )}
      <span className="fw-medium">{message}</span>
    </div>
  );

  const renderMenuCard = (menu) => (
    // Bootstrap Card structure
    <div key={menu._id} className="card shadow-sm border-0 h-100">
      <div className="card-body d-flex align-items-start">
        {/* Document Icon/Thumbnail */}
        <FileText size={48} className="text-danger me-3 flex-shrink-0" />

        {/* Content */}
        <div className="flex-grow-1">
          <h5 className="card-title mb-1">{menu.name}</h5>
          <p className="card-text text-muted small">
            <a href={menu.docUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-underline">
                View/Download E-Menu
            </a>
          </p>
          <small className="text-secondary">
            Uploaded: {new Date(menu.uploadedAt).toLocaleDateString()}
          </small>
        </div>

        {/* Actions */}
        <div className="d-flex flex-column ms-3 flex-shrink-0">
          
          {/* NEW: Show QR Button */}
          <button
              onClick={() => handleShowQr(menu)}
              className="btn btn-info btn-sm mb-2 text-white d-flex align-items-center justify-content-center"
              title="Show Printable QR Code"
              disabled={loading}
          >
              <i className="bi bi-qr-code-scan me-1"></i> QR
          </button>
          
          <button
            onClick={() => handleOpenModal(menu)}
            className="btn btn-outline-primary btn-sm mb-2"
            title="Edit Name/Upload New Document"
            disabled={loading}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleConfirmDelete(menu)}
            className="btn btn-outline-danger btn-sm"
            title="Delete E-Menu"
            disabled={loading}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  // --- Confirmation Dialog (same as before) ---
  const ConfirmationDialog = () =>
    showConfirm && (
      <div
        className="modal d-block fade show"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <div className="modal-dialog modal-sm modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3 text-center">
            <div className="modal-body p-4">
              <AlertTriangle size={48} className="text-danger mx-auto mb-3" />
              <h5 className="modal-title mb-2">Confirm Deletion</h5>
              <p className="text-muted small">
                Are you sure you want to delete E-Menu:{" "}
                <strong>{itemToDelete?.name}</strong>? This will remove the PDF document from storage. This action cannot be undone.
              </p>
            </div>

            <div className="modal-footer justify-content-center border-top-0">
              <button
                onClick={handleCancelDelete}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger d-flex align-items-center"
                disabled={loading}
              >
                {loading ? (
                  <Loader2
                    size={18}
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <Trash2 size={18} className="me-2" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  // --- END Confirmation Dialog ---


  return (
    // Bootstrap Container for main layout
    <div className="">
      {/* Header and Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="text-secondary">
          Manage the e-menu documents for your company.
        </p>
        <button
          onClick={() => handleOpenModal(null)}
          className="btn btn-success d-flex align-items-center shadow-sm"
          disabled={loading}
        >
          <UploadCloud size={20} className="me-2" /> Upload New Menu
        </button>
      </div>

      {/* Status Messages */}
      {globalError && renderStatusMessage(globalError, "error")}
      {globalSuccess && renderStatusMessage(globalSuccess, "success")}
      {loading && (
        <div className="text-center p-4 text-primary">
          <Loader2
            size={24}
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />{" "}
          Loading data...
        </div>
      )}

      {/* E-Menus List - Bootstrap Grid */}
      <div className="row g-4">
        {menus.length > 0
          ? menus.map((menu) => (
              <div key={menu._id} className="col-12 mb-2">
                {renderMenuCard(menu)}
              </div>
            ))
          : !loading && (
              <div className="col-12">
                <div className="p-5 text-center border border-dashed rounded text-muted">
                  No e-menus found. Click "Upload New Menu" to get started.
                </div>
              </div>
            )}
      </div>

      {/* Modals and Dialogs */}
      <AddEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        menuToEdit={menuToEdit}
        isLoading={loading}
      />

      <ConfirmationDialog />

      {/* NEW: QR Display/Print Modal */}
      <QRDisplayModal
          isOpen={isQRModalOpen}
          onClose={handleCloseQRModal}
          menu={menuToShowQR}
          company={COMPANY_INFO}
      />
    </div>
  );
}